import { Component } from 'react'
import { REPORT_PROBLEM_ENABLED } from '../config/featureFlags'
import CrashFallback, { FallbackGuard } from './support/CrashFallback'

/**
 * ErrorBoundary — Catches render-phase crashes and logs component stack.
 * 
 * Added v116.10.1 to trace TDZ "Cannot access 've' before initialization" crash
 * that occurs during re-render after updateAndSaveCase({ status: 'submitted' }).
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    // This is the gold — componentStack shows the React component tree
    console.error('═══════════════════════════════════════════')
    console.error('🔴 ErrorBoundary caught render crash')
    console.error('═══════════════════════════════════════════')
    console.error('Error:', error?.message || error)
    console.error('Error name:', error?.name)
    console.error('Full error:', error)
    console.error('───────────────────────────────────────────')
    console.error('Component Stack (which component crashed):')
    console.error(errorInfo?.componentStack)
    console.error('───────────────────────────────────────────')
    console.error('JS Stack trace:')
    console.error(error?.stack)
    console.error('═══════════════════════════════════════════')

    this.setState({ errorInfo })
  }

  render() {
    if (this.state.hasError) {
      // ★★ REP-10 PA-4: behind REPORT_PROBLEM_ENABLED (OFF), the crash screen becomes a plain
      //   translated sentence with "Report this problem" (components/support/CrashFallback.jsx).
      //   Flag off: the branch below is never taken and this screen renders exactly as before.
      //   CrashFallback is given the two button handlers and this legacy screen — never the
      //   error — and shows the legacy screen itself under es or if it fails.
      if (REPORT_PROBLEM_ENABLED) {
        const legacy = this.renderLegacy()
        return (
          <FallbackGuard legacy={legacy}>
            <CrashFallback
              legacy={legacy}
              onRetry={() => { this.setState({ hasError: false, error: null, errorInfo: null }) }}
              onHome={() => { window.location.href = '/home' }}
            />
          </FallbackGuard>
        )
      }
      return this.renderLegacy()
    }

    return this.props.children
  }

  renderLegacy() {
    {
      const { error, errorInfo } = this.state
      // ★★★ PROD-1156: the error message and both stacks are DEVELOPER output and can hold patient
      //   data (a thrown value can quote anything the screen held, e.g. a JSON.parse error quotes
      //   its input). Outside a dev build they go to the console only (componentDidCatch above),
      //   never onto the screen. check-report-problem.mjs F4 renders this in production polarity.
      const showDetail = import.meta.env.DEV === true
      return (
        <div style={{ padding: 24, fontFamily: 'Inter, system-ui, sans-serif', maxWidth: 600, margin: '40px auto' }}>
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: 20 }}>
            <h2 style={{ color: '#991B1B', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
              Something went wrong
            </h2>
            {showDetail && (
              <p style={{ color: '#B91C1C', fontSize: 13, marginBottom: 16 }}>
                {error?.message || 'Unknown error'}
              </p>
            )}
            
            {/* Component stack — the key diagnostic (dev builds only) */}
            {showDetail && errorInfo?.componentStack && (
              <details open style={{ marginBottom: 12 }}>
                <summary style={{ color: '#991B1B', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginBottom: 4 }}>
                  Component Stack (which component crashed)
                </summary>
                <pre style={{ 
                  background: '#1E293B', color: '#F8FAFC', fontSize: 11, padding: 12, 
                  borderRadius: 8, overflow: 'auto', maxHeight: 300, whiteSpace: 'pre-wrap' 
                }}>
                  {errorInfo.componentStack}
                </pre>
              </details>
            )}

            {/* JS stack (dev builds only) */}
            {showDetail && error?.stack && (
              <details style={{ marginBottom: 16 }}>
                <summary style={{ color: '#991B1B', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginBottom: 4 }}>
                  JS Stack Trace
                </summary>
                <pre style={{ 
                  background: '#1E293B', color: '#F8FAFC', fontSize: 10, padding: 12, 
                  borderRadius: 8, overflow: 'auto', maxHeight: 200, whiteSpace: 'pre-wrap' 
                }}>
                  {error.stack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }) }}
                style={{ 
                  background: '#46A8F9', color: 'white', border: 'none', borderRadius: 8, 
                  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' 
                }}
              >
                Try Again
              </button>
              <button
                onClick={() => { window.location.href = '/home' }}
                style={{ 
                  background: '#E2E8F0', color: '#334155', border: 'none', borderRadius: 8, 
                  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' 
                }}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      )
    }
  }
}
